import { ChartNoAxesColumn, ClipboardList, Cloud, CloudOff, Table, Terminal } from "lucide-react";
import type { AnalyticsToolbarMode } from "../../../../stores/tars/tars-analytics-store";
import { FACET_WS } from "./facet-workspace-theme";
import { FacetChartTypeToolbarIcon } from "./facet-chart-toolbar-icons";
import ChartTypePickerPopover from "./ChartTypePickerPopoverPanel";
import {
    TARS_CHART_LEFT_TO_PUSH_LABEL,
    TARS_CHART_NOT_PUSHED_STATUS_LABEL,
    TARS_CHART_PUSHED_STATUS_LABEL,
} from "../../tars-constants";

const CHART_PILL_LIME = "#BEF264";

export interface FacetToolbarChartSummary {
    type: string;
    title?: string;
    pushed?: boolean;
}

interface FacetWorkspaceToolbarProps {
    facetName: string;
    mode: AnalyticsToolbarMode;
    onModeChange: (mode: AnalyticsToolbarMode) => void;
    canOpenDerivedViews: boolean;
    /** Called when user picks a chart type from the Chart+ popover. */
    onSelectChartType: (chartTypeId: string) => void;
    chartSummaries?: FacetToolbarChartSummary[];
    /** From facet `more.charts`: show chart-type strip only when this is true after load. */
    facetHasCharts: boolean;
    selectedChartIndex?: number;
    onSelectChartIndex?: (index: number) => void;
}

type ModeKey = AnalyticsToolbarMode;

export default function FacetWorkspaceToolbar({
    facetName,
    mode,
    onModeChange,
    canOpenDerivedViews,
    onSelectChartType,
    chartSummaries = [],
    facetHasCharts,
    selectedChartIndex = 0,
    onSelectChartIndex,
}: FacetWorkspaceToolbarProps) {
    const chartActive = mode === "chart";
    const formActive = mode === "form";
    const jsonActive = mode === "json";
    const tableActive = mode === "table";
    const showChartIcons = facetHasCharts && chartSummaries.length > 0 && onSelectChartIndex != null;
    const notPushedCount = chartSummaries.filter((c) => !c.pushed).length;

    const seg = (active: boolean, disabled: boolean) =>
        `relative flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-semibold transition-all ${
            disabled
                ? "cursor-not-allowed opacity-40 text-slate-400"
                : active
                  ? "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200/80"
                  : "text-slate-500 hover:bg-white/60 hover:text-slate-800"
        }`;

    const onSegment = (m: ModeKey) => {
        if (m !== "json" && m !== "form" && !canOpenDerivedViews) return;
        onModeChange(m);
    };

    const chartPlusPopover = (
        <ChartTypePickerPopover
            onSelectChartType={onSelectChartType}
            canOpenDerivedViews={canOpenDerivedViews}
            showChartIcons={showChartIcons}
            segmentButtonClassName={seg(chartActive, !canOpenDerivedViews)}
        />
    );

    return (
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200/80 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
            <h2 className="truncate text-[13px] font-semibold tracking-tight text-slate-800">{facetName}</h2>

            <nav
                className="flex shrink-0 items-center gap-0.5 rounded-2xl p-1 shadow-inner"
                style={{ background: FACET_WS.segmentTrack }}
                aria-label="Analytics view mode"
            >
                <button
                    type="button"
                    className={seg(formActive, false)}
                    onClick={() => onSegment("form")}
                    aria-label="Form view"
                    aria-pressed={formActive}
                >
                    <ClipboardList size={15} strokeWidth={2} className="shrink-0" aria-hidden />
                    <span className="hidden sm:inline">Form</span>
                </button>

                <button
                    type="button"
                    className={seg(jsonActive, false)}
                    onClick={() => onSegment("json")}
                    aria-label="JSON view"
                    aria-pressed={jsonActive}
                >
                    <Terminal size={15} strokeWidth={2} className="shrink-0" aria-hidden />
                    <span className="hidden sm:inline">JSON</span>
                </button>

                <button
                    type="button"
                    className={seg(tableActive, !canOpenDerivedViews)}
                    onClick={() => onSegment("table")}
                    disabled={!canOpenDerivedViews}
                    aria-label="Table view"
                    aria-pressed={tableActive}
                >
                    <Table size={15} strokeWidth={2} className="shrink-0" aria-hidden />
                    <span className="hidden sm:inline">Table</span>
                </button>

                <div className="flex items-center gap-1 self-center">
                    {showChartIcons ? (
                        <div className="flex items-center gap-1.5">
                            <div
                                className="inline-flex h-8 max-h-8 min-h-8 flex-nowrap items-center gap-1 rounded-full px-1 py-0"
                                style={{ background: CHART_PILL_LIME }}
                            >
                                <ul className="m-0 flex h-8 list-none flex-row items-center gap-0.5 gap-1 p-0">
                                    {chartSummaries.map((c, i) => {
                                        const pushed = Boolean(c.pushed);
                                        const statusLabel = pushed
                                            ? TARS_CHART_PUSHED_STATUS_LABEL
                                            : TARS_CHART_NOT_PUSHED_STATUS_LABEL;
                                        return (
                                            <li
                                                key={`${c.type}-${i}`}
                                                className="relative flex h-7 shrink-0 items-center"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onSelectChartIndex(i);
                                                        onSegment("chart");
                                                    }}
                                                    title={`${c.title ?? `Chart ${i + 1}`} — ${statusLabel}`}
                                                    className={`relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 ${
                                                        selectedChartIndex === i
                                                            ? "bg-white text-indigo-700 shadow-md ring-1 ring-indigo-600 ring-offset-1 ring-offset-[#BEF264]"
                                                            : "bg-white/45 text-slate-800 ring-1 ring-slate-900/15 hover:bg-white/85 hover:ring-slate-900/25"
                                                    }`}
                                                    aria-label={`Show chart ${i + 1}, ${statusLabel}`}
                                                    aria-pressed={selectedChartIndex === i}
                                                >
                                                    <FacetChartTypeToolbarIcon chartTypeId={c.type} />
                                                    <span
                                                        className={`absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full text-white shadow-sm ring-1 ring-white ${
                                                            pushed ? "bg-emerald-600" : "bg-amber-500"
                                                        }`}
                                                        aria-hidden
                                                    >
                                                        {pushed ? (
                                                            <Cloud size={8} strokeWidth={3} />
                                                        ) : (
                                                            <CloudOff size={8} strokeWidth={3} />
                                                        )}
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {chartPlusPopover}
                            </div>
                            {notPushedCount > 0 ? (
                                <span className="hidden whitespace-nowrap text-[11px] font-semibold text-amber-700 sm:inline">
                                    {notPushedCount} {TARS_CHART_LEFT_TO_PUSH_LABEL}
                                </span>
                            ) : null}
                        </div>
                    ) : chartActive ? (
                        chartPlusPopover
                    ) : (
                        <button
                            type="button"
                            className={seg(false, !canOpenDerivedViews)}
                            onClick={() => onSegment("chart")}
                            disabled={!canOpenDerivedViews}
                            aria-label="Chart view"
                            aria-pressed={false}
                        >
                            <ChartNoAxesColumn size={15} strokeWidth={2} className="shrink-0" aria-hidden />
                            <span className="hidden sm:inline">Chart</span>
                        </button>
                    )}
                </div>
            </nav>
        </header>
    );
}
